import {Component, computed, effect, inject, OnInit, signal, untracked} from '@angular/core';
import {toObservable} from '@angular/core/rxjs-interop';
import {MaterialModule} from '../../../core/modules/material.module';
import {SharedModule} from '../../../core/modules/shared.module';
import {PagesContainerComponent} from '../pages-container/pages-container.component';
import {NgxSpinnerService} from 'ngx-spinner';
import moment from 'moment';
import {FuseConfigService} from '../../../core/services/config.service';
import {ActivatedRoute, Router} from '@angular/router';
import {FilterComponent} from '../../../core/components/filter/filter.component';
import {TargetGroupService} from '../../../core/services/targetGroup.service';
import {EventTypeService} from '../../../core/services/eventType.service';
import {EventsService} from '../../../core/services/events.service';
import {PlatformsService} from '../../../core/services/platforms.service';
import {UserService} from '../../../core/services/users.service';
import {debounceTime} from 'rxjs';

const DATE_DIVIDERS: Record<string, string> = {
  oneDay: ', ',
  longEvent: ' - ',
  fewDays: '/ ',
  repeatEvent: ', ',
  default: ', '
};

const DAY_COLORS = {
  today: '#f2f30f7a',
  future: '#00800012',
  past: '#ff000014'
};

@Component({
  selector: 'event-page',
  templateUrl: './event.component.html',
  styleUrls: ['./event.component.scss'],
  imports: [MaterialModule, SharedModule, PagesContainerComponent, FilterComponent],
  providers: [EventsService, TargetGroupService, EventTypeService]
})

export class EventComponent implements OnInit {
  private eventService = inject(EventsService);
  private spinner = inject(NgxSpinnerService);
  private fuseConfig = inject(FuseConfigService);
  private router = inject(Router);
  private targetService = inject(TargetGroupService);
  private eventTypeService = inject(EventTypeService);
  private platformsService = inject(PlatformsService);
  private userService = inject(UserService);

  private route = inject(ActivatedRoute);
  eventStatus = signal<string | null>(null);

  events = signal([]);
  pageIndex = signal(0);
  perPage = signal(50);
  sort = signal('createdAt');
  direction = signal('desc');
  search = signal('');
  platformId = this.fuseConfig.selectedPlatformId_Sig;
  pagination = signal<any>(null);
  role = this.fuseConfig.role_Sig;
  userId = this.fuseConfig.userId_Sig;
  filterData = signal<any>({
    publicEvents: true,
    eventRegistration: true,
    mileStoneEvent: true,
    migrate: true,
    myEvents: true
  });
  userFiltersFromDB = signal<any>({});
  userFilters = signal<any>({});
  userFilterId = signal(null);

  readonly displayedColumns = computed(() => {
    const superAdmin = this.role() === 'superAdmin';
    const countryAdmin = this.role() === 'countryAdmin';
    const subCountryAdmin = this.role() === 'subCountryAdmin';
    const organisationAdmin = this.role() === 'organisationAdmin';
    if (superAdmin) {
      this.getPlatforms();
    }
    return [
      'title',
      'type',
      ...(superAdmin || countryAdmin ? ['organization', 'canton'] : []),
      ...(subCountryAdmin ? ['organization'] : []),
      ...(organisationAdmin ? ['merge'] : []),
      'counts',
      'online',
      'date',
      'copy'
    ];
  });

  constructor() {
    this.route.params.subscribe(params => {
      this.eventStatus.set(params['status'] || null);
    });
    effect(() => {
      const s = this.search();
      untracked(() => {
        if (this.pageIndex() !== 0) {
          this.pageIndex.set(0);
        }
      });
    });

    const trigger = computed(() => {
      return {
        page: this.pageIndex(),
        size: this.perPage(),
        sort: this.sort(),
        dir: this.direction(),
        search: this.search(),
        platform: this.platformId(),
        countryId: this.eventService.countryId(),
        citiesId: this.eventService.citiesId(),
        organizationsId: this.eventService.organizationsId(),
        eventTypes: this.eventService.eventTypes(),
        publicEvents: this.eventService.publicEvents(),
        eventWithRegistration: this.eventService.eventRegistration(),
        targetGroups: this.eventService.targetGroups(),
        mileStoneEvent: this.eventService.milestone(),
        myEvents: this.eventService.myEvents(),
        status: this.eventStatus()
      };
    });

    toObservable(trigger).pipe(debounceTime(300)).subscribe(() => {
      this.getEvents();
    });

    this.eventService.setToAll();
  }


  ngOnInit() {
    this.getCountries();
    this.getTargetGroups();
    this.getEventTypes();
    this.getUserFilters();
  }

  getUserFilters() {
    this.userService.getFilter('event').subscribe((res: any) => {
      if (res) {
        this.userFiltersFromDB.set(JSON.parse(res.config));
        this.userFilters.set(JSON.parse(res.config));
        this.userFilterId.set(res.id);
      }
    });
  }

  getCountries() {
    this.eventService.getCountry().subscribe((res: any) => {
      if (res.length === 1) {
        this.getCantons(res[0].id);
      } else {
        this.filterData.update(f => ({...f, countries: res}));
      }
    });
  }

  getCantons(id) {
    this.eventService.getCity(id).subscribe((res: any) => {
      if (res.length === 1) {
        this.eventService.setCities({selected: true, canton: res[0].id});
        this.getOrganizations();
      } else {
        this.filterData.update(f => ({...f, cantons: res}));
      }
    });
  }

  getOrganizations() {
    this.eventService.getOrganizationByCantons().subscribe((res: any) => {
      if (res.length === 1) {
        this.eventService.setOrganizations({selected: true, organization: res[0].id});
      } else {
        this.filterData.update(f => ({...f, organizations: res}));
      }
    });
  }

  getPlatforms() {
    this.platformsService.getPlatforms().subscribe((res: any) => {
      this.filterData.update(f => ({...f, platforms: res}));
    });
  }

  getTargetGroups() {
    this.targetService.getTargetGroups(false).then((data: any) => {
      this.filterData.update(f => ({...f, targetGroups: data.targetGroups}));
    });
  }

  getEventTypes() {
    this.eventTypeService.getEventTypeDropDown().subscribe((data: any) => {
      this.filterData.update(f => ({...f, eventTypes: data}));
    });
  }

  chooseFilters(e) {
    if (e.choose.hasOwnProperty('country')) {
      this.eventService.setCountryId(e.choose.country);
      this.getCantons(e.choose.country);
    } else if (e.choose.hasOwnProperty('canton')) {
      this.eventService.setCities(e.choose);
      this.getOrganizations();
    } else if (e.choose.hasOwnProperty('organization')) {
      this.eventService.setOrganizations(e.choose);
    } else if (e.choose.hasOwnProperty('targetGroups')) {
      this.eventService.setTargetGroups(e.choose);
    } else if (e.choose.hasOwnProperty('eventType')) {
      this.eventService.setEventTypes(e.choose);
    } else if (e.choose.hasOwnProperty('publicEvents')) {
      this.eventService.setPublic(e.choose.publicEvents);
    } else if (e.choose.hasOwnProperty('eventRegistration')) {
      this.eventService.setRegistration(e.choose.selected);
    } else if (e.choose.hasOwnProperty('mileStoneEvent')) {
      this.eventService.setMilestone(e.choose.selected);
    } else if (e.choose.hasOwnProperty('myEvents')) {
      this.eventService.setMyEvents(e.choose.selected);
    } else if (e.choose.hasOwnProperty('platforms')) {
      this.platformId.set(e.choose.platforms);
    } else if (e.choose === 'all') {
      this.eventService.setToAll();
      this.filterData.update(f => {
        const {organizations, cantons, ...rest} = f;
        return rest;
      });
    }
    if (e.choose === 'all') {
      this.fuseConfig.activeFilter_Sig.set(false);
    } else {
      this.fuseConfig.activeFilter_Sig.set(true);
    }
    this.setDataFromFilter(e);
  }

  handleSearch(value) {
    this.search.set(value);
  }

  handlePageEvent(e) {
    this.pageIndex.set(e.pageIndex);
    this.perPage.set(e.pageSize);
  }

  getEvents() {
    this.spinner.show();
    const status = this.eventStatus();
    const isOld = status === 'archive';
    const isRunning = status === 'running';
    const isDeleted = status === 'deleted';
    this.eventService.getEvents(this.pageIndex() + 1, this.perPage(), this.sort(), this.direction(),
      this.search(), isOld, isRunning, isDeleted, this.platformId() || '').subscribe({
      next: (res: any) => {
        const processed = res.events.map(event => this.formatEvent(event));

        this.events.set(processed);
        this.pagination.set(res.pagination);

        this.spinner.hide();
        this.restoreScroll();
      },
      error: (err) => {
        this.spinner.hide();
      }
    });
  }

  sortData(e) {
    if (!e.direction) {
      this.sort.set('createdAt');
      this.direction.set('desc');
    } else {
      this.sort.set(e.active);
      this.direction.set(e.direction);
    }
    localStorage.setItem('eventSort', this.sort());
    localStorage.setItem('eventSortDirection', this.direction());
    this.restoreScroll();
  }

  setDataFromFilter(e) {
    if (e.choose === 'all') {
      this.userFilters.set({});
      this.userFiltersFromDB.set({});
    } else if (e.choose.hasOwnProperty('country')) {
      this.userFilters.update(filters => {
        const {organizations, cantons, ...rest} = filters;
        return {...rest, country: e.choose.country};
      });
    } else if (e.choose.hasOwnProperty('canton')) {
      const _cities = this.eventService.getCities();
      this.userFilters.update(filters => ({...filters, cantons: _cities}));
    } else if (e.choose.hasOwnProperty('organization')) {
      const _organizations = this.eventService.getOrganizations();
      this.userFilters.update(filters => ({...filters, organizations: _organizations}));
    } else if (e.choose.hasOwnProperty('targetGroups')) {
      const _targetGroups = this.eventService.getTargetGroups();
      this.userFilters.update(filters => ({...filters, targetGroups: _targetGroups}));
    } else if (e.choose.hasOwnProperty('eventType')) {
      const _eventType = this.eventService.getEventTypes();
      this.userFilters.update(filters => ({...filters, eventTypes: _eventType}));
    } else if (e.choose.hasOwnProperty('eventRegistration')) {
      this.userFilters.update(filters => ({...filters, eventRegistration: e.choose.selected}));
    } else if (e.choose.hasOwnProperty('publicEvents')) {
      this.userFilters.update(filters => ({...filters, publicEvents: e.choose.publicEvents}));
    } else if (e.choose.hasOwnProperty('platforms')) {
      this.userFilters.update(filters => ({...filters, platforms: e.choose.platforms}));
    } else if (e.choose.hasOwnProperty('mileStoneEvent')) {
      this.userFilters.update(filters => ({...filters, mileStoneEvent: e.choose.selected}));
    } else if (e.choose.hasOwnProperty('myEvents')) {
      this.userFilters.update(filters => ({...filters, myEvents: e.choose.selected}));
    }
    if ((this as any).filterTimeout) {
      clearTimeout((this as any).filterTimeout);
    }

    (this as any).filterTimeout = setTimeout(() => {
      this.setUserFilters();
    }, 1000);
  }

  setUserFilters() {
    this.userService.setFilters('event', this.userFilters(), this.userFilterId() || '')
      .subscribe((res: any) => {
      });
  }

  private formatEvent(event: any) {
    const divider = DATE_DIVIDERS[event.datesType] ?? DATE_DIVIDERS.default;
    const dateStrings = event.eventDates || [];
    const sortedData = dateStrings.sort((a, b) => a.date.localeCompare(b.date));
    const firstDate = sortedData.length ? moment(new Date(sortedData[0].date), 'DD.MM.YYYY') : null;

    const dateForShow = sortedData
      .map(d => moment(new Date(d.date)).format('dddd, DD. MMMM YYYY'))
      .join(divider);

    return {
      ...event,
      dateForShow,
      dayColor: this.getEventColor(firstDate)
    };
  }

  private getEventColor(eventDate: moment.Moment | null): string {
    if (!eventDate) {
      return DAY_COLORS.future;
    }

    const today = moment().startOf('day');
    const date = eventDate.clone().startOf('day');

    if (date.isSame(today)) {
      return DAY_COLORS.today;
    }
    return date.isAfter(today) ? DAY_COLORS.future : DAY_COLORS.past;
  }

  private restoreScroll() {
    localStorage.setItem('eventScroll', '0');
    const table = document.getElementById('eventPage');
    if (table) {
      table.scrollTop = 0;
    }
  }

  copyEvent(event, id, merge) {
    if (!merge) {
      event.stopPropagation();
      const queryParams = {
        copyId: id,
        ...(this.role() === 'organisationAdmin' && this.userId() ? {userId: this.userId()} : {})
      };
      void this.router.navigate(['/pages/event/detail'], {queryParams});
    }
  }

  onScroll(e) {
    localStorage.setItem('eventScroll', e.target.scrollTop);
  }
}
